import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, RefreshCw, AlertCircle, Calendar, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtMoney(cents) {
    if (cents === null || cents === undefined) return "—";
    return `$${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return iso;
    }
}

export default function AutoRenewManage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/me/auto-renews`, { withCredentials: true });
            setData(r.data);
        } catch (e) {
            toast.error("Couldn't load your billing settings", { description: e?.response?.data?.detail });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = async (creditId, next) => {
        setBusyId(creditId);
        try {
            await axios.post(
                `${API}/me/auto-renews/${creditId}/toggle`,
                { auto_renew: next },
                { withCredentials: true },
            );
            toast.success(next ? "Auto-renew is on" : "Auto-renew turned off");
            await load();
        } catch (e) {
            toast.error("Couldn't update", { description: e?.response?.data?.detail });
        } finally {
            setBusyId(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center text-zinc-300">
                Loading your billing…
            </div>
        );
    }

    const credits = data?.credits || [];
    const subs = data?.subscriptions || [];
    const commitments = data?.commitments || [];

    return (
        <div className="min-h-screen bg-black text-white">
            <div className="max-w-3xl mx-auto px-5 py-8">
                <Link
                    to="/dashboard"
                    data-testid="back-to-dashboard"
                    className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase text-zinc-400 hover:text-brand-red mb-6"
                >
                    <ChevronLeft className="w-3.5 h-3.5" /> Dashboard
                </Link>

                <div className="mb-2 text-[10px] tracking-[0.3em] uppercase text-brand-red">Billing</div>
                <h1 className="text-3xl sm:text-4xl font-display mb-1">Auto-Renew & Renewals</h1>
                <p className="text-sm text-zinc-300 max-w-xl mb-8 leading-relaxed">
                    Control how your packs renew. Turn auto-renew off any time — sessions you've already
                    paid for stay yours. We charge only when the last session is used, never on a calendar date.
                </p>

                {/* ---------------- Credit Packs ---------------- */}
                <section className="mb-10">
                    <div className="flex items-center gap-2 mb-4">
                        <Zap className="w-4 h-4 text-brand-red" />
                        <h2 className="text-[11px] tracking-[0.28em] uppercase text-brand-red">Session Packs</h2>
                    </div>
                    {credits.length === 0 ? (
                        <div className="border border-zinc-800 rounded-2xl bg-zinc-950/50 p-6 text-sm text-zinc-400">
                            No active session packs. Buy a pack and it'll show up here.
                        </div>
                    ) : (
                        <ul className="space-y-3" data-testid="credits-list">
                            {credits.map((c) => {
                                const remaining = c.remaining_sessions;
                                const onLast = remaining === 1;
                                const isBusy = busyId === c.credit_id;
                                return (
                                    <li
                                        key={c.credit_id}
                                        data-testid={`credit-${c.credit_id}`}
                                        className="border border-zinc-800 rounded-2xl bg-zinc-950/60 p-5"
                                    >
                                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold text-white">{c.pack_name}</div>
                                                <div className="text-[12px] text-zinc-400 mt-1">
                                                    {remaining} of {c.total_sessions} sessions remaining · {fmtMoney(c.amount_paid_cents)} pack
                                                </div>
                                                {c.expires_at && (
                                                    <div className="text-[11px] text-zinc-500 mt-0.5">
                                                        Expires {fmtDate(c.expires_at)}
                                                    </div>
                                                )}
                                                {onLast && c.auto_renew && (
                                                    <div className="mt-3 text-[12px] text-amber-200 bg-amber-900/15 border border-amber-700/40 rounded-lg px-3 py-2 leading-relaxed">
                                                        <b>Heads up</b> — your next session is the last on this pack. After it's used,
                                                        we'll auto-renew at <b>{fmtMoney(c.amount_paid_cents)}</b>.
                                                    </div>
                                                )}
                                                {c.last_renewal_failed_reason && (
                                                    <div className="mt-3 text-[12px] text-red-300 bg-red-900/15 border border-red-700/40 rounded-lg px-3 py-2 leading-relaxed flex items-start gap-2">
                                                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                                        <div>
                                                            <div className="font-semibold">Last renewal didn't go through</div>
                                                            <div className="text-zinc-300 mt-0.5">{c.last_renewal_failed_reason}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="shrink-0">
                                                <button
                                                    type="button"
                                                    data-testid={`toggle-${c.credit_id}`}
                                                    disabled={isBusy}
                                                    onClick={() => toggle(c.credit_id, !c.auto_renew)}
                                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[11px] tracking-[0.16em] uppercase font-semibold transition-colors ${
                                                        c.auto_renew
                                                            ? "bg-brand-red text-white hover:bg-brand-red/80"
                                                            : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                >
                                                    <RefreshCw className={`w-3 h-3 ${isBusy ? "animate-spin" : ""}`} />
                                                    {c.auto_renew ? "Auto-renew: ON" : "Auto-renew: OFF"}
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>

                {/* ---------------- Subscriptions / Commitments ---------------- */}
                <section className="mb-10">
                    <div className="flex items-center gap-2 mb-4">
                        <Calendar className="w-4 h-4 text-brand-red" />
                        <h2 className="text-[11px] tracking-[0.28em] uppercase text-brand-red">Memberships</h2>
                    </div>
                    {commitments.length === 0 && subs.length === 0 ? (
                        <div className="border border-zinc-800 rounded-2xl bg-zinc-950/50 p-6 text-sm text-zinc-400">
                            No active memberships.
                        </div>
                    ) : (
                        <ul className="space-y-3" data-testid="subs-list">
                            {commitments.map((c) => {
                                const endsAt = c.commitment_ends_at;
                                const now = new Date();
                                const ends = endsAt ? new Date(endsAt) : null;
                                const daysOut = ends ? Math.round((ends - now) / (1000 * 60 * 60 * 24)) : null;
                                const isClose = daysOut !== null && daysOut <= 7 && daysOut >= 0;
                                return (
                                    <li
                                        key={c.commitment_id}
                                        data-testid={`commitment-${c.commitment_id}`}
                                        className="border border-zinc-800 rounded-2xl bg-zinc-950/60 p-5"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold text-white">{c.display_name || c.sku_id}</div>
                                                <div className="text-[12px] text-zinc-400 mt-1">
                                                    Status: <span className="text-zinc-200">{c.status}</span>
                                                    {endsAt && <> · Ends {fmtDate(endsAt)}</>}
                                                </div>
                                                {isClose && (
                                                    <div className="mt-3 text-[12px] text-amber-200 bg-amber-900/15 border border-amber-700/40 rounded-lg px-3 py-2 leading-relaxed">
                                                        <b>One week left</b> — your membership ends {fmtDate(endsAt)}. Renew below to keep training.
                                                    </div>
                                                )}
                                            </div>
                                            <Link
                                                to="/dashboard"
                                                data-testid="renew-link"
                                                className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[11px] tracking-[0.16em] uppercase font-semibold bg-zinc-800 text-zinc-200 hover:bg-zinc-700"
                                            >
                                                Manage
                                            </Link>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>

                <div className="text-[11px] text-zinc-500 leading-relaxed border-t border-zinc-900 pt-5 mt-10">
                    Auto-renew charges your card the moment your last session is consumed — never on a calendar date.
                    If a charge fails, your athletes can still train; you'll get an email to update your card.
                </div>
            </div>
        </div>
    );
}
